import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { MapPin, ShieldCheck, ArrowLeft, Send, Check, Trophy, Star } from "lucide-react";
import { toast } from "sonner";
import Logo from "../components/Logo";
import { api, formatApiError } from "../lib/api";
import { CITIES } from "../lib/cities";

export default function FindAgent() {
  const cities = CITIES;
  const [city, setCity] = useState(CITIES[0]);
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [requestFor, setRequestFor] = useState(null);
  const [form, setForm] = useState({ name: "", phone: "", requirement: "" });
  const [busy, setBusy] = useState(false);
  const [sentTo, setSentTo] = useState({});

  useEffect(() => { if (!city) { setAgents([]); return; }
    (async () => { setLoading(true); try { const { data } = await api.get(`/public/find-agent?city=${encodeURIComponent(city)}`); setAgents(data); } finally { setLoading(false); } })();
  }, [city]);

  const submitLead = async () => {
    if (!form.name || !form.phone) return toast.error("Name and phone are required.");
    setBusy(true);
    try {
      await api.post(`/public/lead/${requestFor.id}`, { ...form, city });
      setSentTo({ ...sentTo, [requestFor.id]: true });
      setRequestFor(null); setForm({ name: "", phone: "", requirement: "" });
      toast.success(`Sent! ${requestFor.display_name} will reach out shortly.`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen" data-testid="find-agent-page" style={{ background: "#080808", color: "#F5F5F5" }}>
      <header className="border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-6 py-5 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3 text-gray-500 hover:text-white"><ArrowLeft className="h-4 w-4" /> <Logo size="sm" /></Link>
          <div className="flex items-center gap-4 text-sm">
            <Link to="/top-agents" className="text-gray-500 hover:text-white flex items-center gap-1.5" data-testid="link-top-agents"><Trophy className="h-3.5 w-3.5" /> Leaderboard</Link>
            <Link to="/login" className="text-[#F97316] font-medium">Login</Link>
          </div>
        </div>
      </header>

      <section className="py-12" style={{ background: "#111111" }}>
        <div className="max-w-3xl mx-auto px-6">
          <p className="text-xs font-semibold tracking-widest uppercase mb-4" style={{ color: "#F97316" }}>Find a Broker</p>
          <h1 className="text-4xl md:text-5xl font-bold font-display tracking-tight">Work with a broker you can trust.</h1>
          <p className="mt-4 text-gray-400 text-lg leading-relaxed">Every broker on Orello is verified and rated by real clients. They handle your agreement, deposit, and condition report — all on one platform. No side deals. No WhatsApp chaos.</p>
          <div className="mt-8 grid grid-cols-3 gap-4">
            {[
              { icon: "✓", title: "Verified identity", desc: "Every broker is Aadhaar-verified before they can list on Orello" },
              { icon: "⭐", title: "Real ratings", desc: "Ratings come from completed deals only — no fake reviews" },
              { icon: "🔒", title: "Protected deals", desc: "All deals include escrow, legal agreements, and dispute resolution" },
            ].map((item) => (
              <div key={item.title} className="p-4 rounded-xl" style={{ background: "rgba(249,115,22,0.05)", border: "1px solid rgba(249,115,22,0.15)" }}>
                <div className="text-xl mb-2">{item.icon}</div>
                <div className="text-sm font-semibold text-white mb-1">{item.title}</div>
                <div className="text-xs text-gray-500 leading-relaxed">{item.desc}</div>
              </div>
            ))}
          </div>

          <div className="mt-8 card-ink p-5">
            <label className="text-sm font-medium">Your city</label>
            <select data-testid="city-select" value={city} onChange={(e) => setCity(e.target.value)} className="mt-1 w-full border border-gray-700 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-100 focus:border-indigo-500">
              {cities.length === 0 ? <option>No cities yet</option> : cities.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>

          <div className="mt-8 space-y-3">
            {loading ? <div className="text-gray-500">Loading…</div> : agents.length === 0 ? (
              <div className="text-center py-12 card-ink">
                <p className="text-gray-400 font-medium">No verified agents in {city || "this area"} yet.</p>
                <p className="text-sm text-gray-500 mt-1">Try a different city or <Link to="/register" className="text-[#F97316]">become an agent</Link>.</p>
              </div>
            ) : agents.map((a, i) => (
              <div key={a.id} className="card-ink p-5 flex items-center gap-4" data-testid={`agent-card-${i}`}>
                <div className="h-12 w-12 rounded-full bg-[#F97316] text-white flex items-center justify-center font-semibold text-lg">{a.display_name[0]}</div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold flex items-center gap-2">{a.display_name} <ShieldCheck className="h-4 w-4 text-emerald-500" /></div>
                  <div className="text-xs text-gray-500 flex items-center gap-2 mt-0.5">
                    <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" /> {a.city}</span>
                    <span>·</span>
                    <span>{a.deals_total} total deals · {a.deals_this_month} this month</span>
                    {a.rating && (<><span>·</span><span className="inline-flex items-center gap-0.5 text-amber-600"><Star className="h-3 w-3 fill-amber-400 text-amber-400" /> {a.rating.avg} <span className="text-gray-400">({a.rating.count})</span></span></>)}
                  </div>
                </div>
                {sentTo[a.id] ? (
                  <span className="pill bg-emerald-50 text-emerald-700"><Check className="h-3.5 w-3.5" /> Request sent</span>
                ) : (
                  <button onClick={() => setRequestFor(a)} className="bg-[#F97316] hover:bg-[#EA580C] btn-press btn-orange-primary text-white px-4 py-2 rounded-lg text-sm font-medium inline-flex items-center gap-1.5" data-testid={`request-${i}`}>
                    <Send className="h-3.5 w-3.5" /> Request this agent
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {requestFor && (
        <>
          <div className="fixed inset-0 bg-black/40 z-40" onClick={() => setRequestFor(null)} />
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="rounded-xl shadow-2xl max-w-md w-full p-6" style={{ background: "#111111", border: "1px solid rgba(249,115,22,0.2)", color: "#F5F5F5" }} data-testid="lead-modal">
              <h2 className="text-lg font-semibold font-display">Request {requestFor.display_name}</h2>
              <p className="text-sm text-gray-500 mt-1">Tell {requestFor.display_name} a bit about what you need. They'll reach out by phone or WhatsApp.</p>
              <div className="mt-5 space-y-3">
                <div><label className="text-sm font-medium">Your name</label><input data-testid="lead-name" value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} className="mt-1 w-full border border-gray-700 rounded-lg px-3 py-2.5 text-sm" /></div>
                <div><label className="text-sm font-medium">Mobile</label><input data-testid="lead-phone" value={form.phone} onChange={(e) => setForm({...form, phone: e.target.value})} placeholder="+91…" className="mt-1 w-full border border-gray-700 rounded-lg px-3 py-2.5 text-sm" /></div>
                <div><label className="text-sm font-medium">What are you looking for?</label><textarea data-testid="lead-req" value={form.requirement} onChange={(e) => setForm({...form, requirement: e.target.value})} placeholder="e.g. 2BHK in Koramangala under ₹40k/mo" className="mt-1 w-full border border-gray-700 rounded-lg px-3 py-2.5 text-sm" rows={3} /></div>
              </div>
              <div className="mt-5 flex gap-2 justify-end">
                <button onClick={() => setRequestFor(null)} className="border border-gray-700 px-4 py-2 rounded-lg text-sm font-medium">Cancel</button>
                <button onClick={submitLead} disabled={busy} data-testid="lead-submit" className="bg-[#F97316] hover:bg-[#EA580C] btn-press btn-orange-primary text-white px-5 py-2 rounded-lg text-sm font-medium disabled:opacity-50">{busy ? "Sending…" : "Send Request"}</button>
              </div>
            </div>
          </div>
        </>
      )}

      <footer className="bg-[#0F0F1A] text-white/50 py-8 text-center text-sm">© 2025 Orello — No deposit fights.</footer>
    </div>
  );
}
